import { ChangeDetectorRef, Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AgentService } from '../services/agent.service';
import { AuthenticationService } from 'app/_services/authentication.service';
import { ChatbotStateService } from '../services/chatbot-state.service';
import {
  escapePlainTextForHtml,
  parseAndSanitizeMarkdown,
} from 'app/_utils/safe-markdown';
import { buildAdminNavigationPromptContext } from '../admin-navigation.util';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

@Component({
  selector: 'app-chatbot-widget',
  templateUrl: './chatbot-widget.component.html',
  styleUrls: ['./chatbot-widget.component.scss']
})
export class ChatbotWidgetComponent implements OnInit, OnDestroy {
  @Output() widgetClosed = new EventEmitter<void>();

  messages: Message[] = [];
  userInput = '';
  isLoading = false;
  sessionId: string;
  currentUser: any;
  currentUserName = '';

  constructor(
    private agentService: AgentService,
    private authService: AuthenticationService,
    private stateService: ChatbotStateService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.sessionId = this.agentService.generateSessionId();
    this.currentUser = this.authService.getCurrentUser();
    this.currentUserName = this.buildUserDisplayName();
  }

  ngOnInit() {
    const savedState = this.stateService.loadState();

    if (savedState?.messages?.length) {
      this.messages = savedState.messages;
    }
    if (savedState?.sessionId) {
      this.sessionId = savedState.sessionId;
    }

    if (this.messages.length === 0) {
      this.messages.push({
        role: 'assistant',
        content: `👋 Hello${this.currentUserName ? ` ${this.currentUserName}` : ''}! I'm your AI assistant. How can I help you today?`,
        timestamp: new Date()
      });
      this.saveState();
    }

    setTimeout(() => this.scrollToBottom(), 100);
  }

  ngOnDestroy() {
    this.saveState();
  }

  private saveState(): void {
    this.stateService.saveState({
      messages: this.messages,
      sessionId: this.sessionId,
    });
  }

  private getBearerToken(): string {
    return this.authService.token() || '';
  }

  private getBureauId(): string | undefined {
    const bureauId = this.authService.current_bureau_id();
    return bureauId != null && bureauId !== '' ? String(bureauId) : undefined;
  }

  async sendMessage(): Promise<void> {
    if (!this.userInput.trim() || this.isLoading) {
      return;
    }

    if (!this.getBearerToken()) {
      this.messages.push({
        role: 'assistant',
        content: 'Error: You must be logged in to use the AI assistant.',
        timestamp: new Date()
      });
      return;
    }

    const userMessage: Message = {
      role: 'user',
      content: this.userInput,
      timestamp: new Date()
    };
    this.messages.push(userMessage);

    const question = this.userInput;
    this.userInput = '';
    this.isLoading = true;

    setTimeout(() => this.scrollToBottom(), 100);

    let assistantMessage: Message | null = null;

    try {
      const response = await this.agentService.invokeAgentStream(
        question,
        this.sessionId,
        this.getBureauId(),
        this.buildPromptOverrides(),
        (token) => {
          if (!assistantMessage) {
            assistantMessage = {
              role: 'assistant',
              content: '',
              timestamp: new Date()
            };
            this.messages.push(assistantMessage);
          }
          assistantMessage.content += token;
          this.cdr.detectChanges();
          this.scrollToBottom();
        }
      );

      if (!assistantMessage) {
        assistantMessage = {
          role: 'assistant',
          content: response || 'No response received from the assistant.',
          timestamp: new Date()
        };
        this.messages.push(assistantMessage);
      } else if (response && !assistantMessage.content) {
        assistantMessage.content = response;
      }
      if (!assistantMessage.content) {
        assistantMessage.content = 'No response received from the assistant.';
      }
    } catch (error) {
      console.error('Error invoking agent:', error);

      const errorText = `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      if (assistantMessage) {
        assistantMessage.content = errorText;
      } else {
        this.messages.push({
          role: 'assistant',
          content: errorText,
          timestamp: new Date()
        });
      }
    } finally {
      this.isLoading = false;
      this.saveState();
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  getAuthStatus(): string {
    const token = this.getBearerToken();
    if (!token) {
      return '❌ Not logged in';
    }
    return `🔐 ${this.currentUserName || 'Logged in'}`;
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearChat(): void {
    this.messages = [{
      role: 'assistant',
      content: `👋 Chat cleared! How can I help you${this.currentUserName ? `, ${this.currentUserName}` : ''}?`,
      timestamp: new Date()
    }];
    this.sessionId = this.agentService.generateSessionId();
    this.saveState();
  }

  closeWidget(): void {
    this.widgetClosed.emit();
  }

  private scrollToBottom(): void {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  private buildUserDisplayName(): string {
    if (!this.currentUser) {
      return '';
    }
    const parts = [
      this.currentUser.first_name,
      this.currentUser.last_name,
    ].filter((part) => !!part);
    if (parts.length > 0) {
      return parts.join(' ');
    }
    return this.currentUser.username || '';
  }

  private buildPromptOverrides(): Record<string, any> | undefined {
    const overrides: Record<string, any> = {};
    if (this.currentUser) {
      overrides.user_id = this.currentUser.id;
      overrides.user_first_name = this.currentUser.first_name;
      overrides.user_last_name = this.currentUser.last_name;
      overrides.user_name = this.buildUserDisplayName();
      overrides.user_role = this.authService.role();
      overrides.company_id = this.authService.current_company_id();
      overrides.bureau_id = this.authService.current_bureau_id();
      overrides.company_name = this.authService.current_company_name();
      overrides.bureau_name = this.authService.current_bureau_name();
    }

    const adminNav = buildAdminNavigationPromptContext(
      typeof window !== 'undefined' ? window.location.origin : '',
      this.authService.current_company_name() || '',
      this.authService.current_bureau_name() || '',
      this.authService.current_company_id() || undefined,
    );
    if (adminNav) {
      Object.assign(overrides, adminNav);
    }

    return Object.keys(overrides).length ? overrides : undefined;
  }

  renderMarkdown(content: string, role: 'user' | 'assistant'): SafeHtml {
    if (role === 'assistant') {
      try {
        const sanitized = parseAndSanitizeMarkdown(content);
        return this.sanitizer.bypassSecurityTrustHtml(sanitized);
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return this.sanitizer.bypassSecurityTrustHtml(escapePlainTextForHtml(content));
      }
    }
    return this.sanitizer.bypassSecurityTrustHtml(escapePlainTextForHtml(content));
  }
}
